/**
 * Orders open in the detail pane, as tabs (AB#21279).
 *
 * The pane held one order; every row click replaced the last, and comparing
 * two orders meant remembering one of them. Up to five are open at once now,
 * the way a support desk holds tickets. Everything that decides what happens
 * to the set lives here as pure functions over plain data, so the rules can be
 * tested without a store, a component or a DOM — the component decides WHEN
 * to ask, this file decides WHAT the answer is.
 *
 * The rules, as agreed on 15 Sep 2026:
 *  - a row click opens a new tab; a row already open switches to its tab;
 *  - at five, a sixth replaces the tab viewed longest ago — but never one
 *    with unsaved input, and if every tab is dirty the sixth is refused;
 *  - closing the active tab falls back to the most recently viewed remaining;
 *  - the set and the active tab survive a refresh.
 */

export const MAX_ORDER_TABS = 5;
export const ORDER_TABS_KEY = 'dashboardOrderTabs';

export interface OrderTab {
  /** `${orderId}:${itemId}` — one tab per order item, which is what a row is. */
  key: string;
  orderId: number;
  itemId: number;
  /** What the tab shows: "#27951", "M_#27950", "#27949-1". */
  label: string;
  /** Second line on the tab: the merchant, truncated. */
  merchant: string;
  openedAt: number;
  viewedAt: number;
  /** Something typed and not saved — a move status picked, a reason entered. */
  dirty: boolean;
  /** Which inner tab (Timeline, Detail, Files…) this order was on. */
  innerTab: string;
}

export interface OrderTabsState {
  tabs: OrderTab[];
  activeKey: string | null;
}

/** What is saved: the set (without the transient `dirty`), plus whose it is. */
interface SavedOrderTabs {
  tabs: Omit<OrderTab, 'dirty'>[];
  activeKey: string | null;
  /**
   * The user the tabs belong to. Storage is per browser, not per person: an
   * admin who impersonates a merchant, or signs out and someone else signs
   * in, would otherwise open the dashboard on the previous person's orders.
   * A set saved for one user is never restored for another.
   */
  ownerId?: number | null;
}

export type OpenResult =
  | { ok: true; state: OrderTabsState; replaced: OrderTab | null }
  | { ok: false; reason: 'all-dirty'; state: OrderTabsState };

export const tabKey = (orderId: number, itemId: number): string => `${orderId}:${itemId}`;

export const emptyTabs = (): OrderTabsState => ({ tabs: [], activeKey: null });

export const activeTab = (state: OrderTabsState): OrderTab | null =>
  state.tabs.find((tab) => tab.key === state.activeKey) ?? null;

/**
 * The tab a sixth order would replace: viewed longest ago, never dirty.
 * Null when every tab is dirty — then nothing may be replaced.
 */
export const replacementCandidate = (tabs: OrderTab[]): OrderTab | null => {
  const clean = tabs.filter((tab) => !tab.dirty);
  if (clean.length === 0) return null;
  return clean.reduce((oldest, tab) => (tab.viewedAt < oldest.viewedAt ? tab : oldest));
};

/**
 * Open an order. Already open → activate it. Room → append. Full → replace
 * the candidate, or refuse when there is none.
 */
export const openTab = (
  state: OrderTabsState,
  incoming: Pick<OrderTab, 'orderId' | 'itemId' | 'label' | 'merchant'> & { innerTab?: string },
  now: number
): OpenResult => {
  const key = tabKey(incoming.orderId, incoming.itemId);
  const existing = state.tabs.find((tab) => tab.key === key);

  if (existing) {
    return { ok: true, state: activateTab(state, key, now), replaced: null };
  }

  const fresh: OrderTab = {
    key,
    orderId: incoming.orderId,
    itemId: incoming.itemId,
    label: incoming.label,
    merchant: incoming.merchant,
    openedAt: now,
    viewedAt: now,
    dirty: false,
    innerTab: incoming.innerTab ?? 'timeline',
  };

  if (state.tabs.length < MAX_ORDER_TABS) {
    return { ok: true, state: { tabs: [...state.tabs, fresh], activeKey: key }, replaced: null };
  }

  const victim = replacementCandidate(state.tabs);
  if (!victim) {
    return { ok: false, reason: 'all-dirty', state };
  }

  // The new tab takes the victim's place rather than the end of the strip, so
  // the strip does not shuffle under the user's pointer.
  const tabs = state.tabs.map((tab) => (tab.key === victim.key ? fresh : tab));
  return { ok: true, state: { tabs, activeKey: key }, replaced: victim };
};

/** Put a replaced tab back where it was. The tab that displaced it goes. */
export const undoReplace = (
  state: OrderTabsState,
  replaced: OrderTab,
  byKey: string
): OrderTabsState => {
  if (!state.tabs.some((tab) => tab.key === byKey)) return state;
  const tabs = state.tabs.map((tab) => (tab.key === byKey ? replaced : tab));
  return { tabs, activeKey: replaced.key };
};

export const activateTab = (state: OrderTabsState, key: string, now: number): OrderTabsState => {
  if (!state.tabs.some((tab) => tab.key === key)) return state;
  return {
    tabs: state.tabs.map((tab) => (tab.key === key ? { ...tab, viewedAt: now } : tab)),
    activeKey: key,
  };
};

/**
 * Close a tab. Closing the active one activates the most recently viewed of
 * what remains — the order the user was on before, in the common case.
 */
export const closeTab = (state: OrderTabsState, key: string): OrderTabsState => {
  const tabs = state.tabs.filter((tab) => tab.key !== key);
  if (tabs.length === state.tabs.length) return state;
  if (state.activeKey !== key) return { tabs, activeKey: state.activeKey };
  if (tabs.length === 0) return { tabs, activeKey: null };
  const next = tabs.reduce((latest, tab) => (tab.viewedAt > latest.viewedAt ? tab : latest));
  return { tabs, activeKey: next.key };
};

export const setDirty = (state: OrderTabsState, key: string, dirty: boolean): OrderTabsState => ({
  ...state,
  tabs: state.tabs.map((tab) => (tab.key === key && tab.dirty !== dirty ? { ...tab, dirty } : tab)),
});

export const setInnerTab = (
  state: OrderTabsState,
  key: string,
  innerTab: string
): OrderTabsState => ({
  ...state,
  tabs: state.tabs.map((tab) => (tab.key === key ? { ...tab, innerTab } : tab)),
});

/* ------------------------------------------------------------------ *
 * Persistence
 * ------------------------------------------------------------------ */

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const isTab = (value: unknown): value is OrderTab => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.orderId === 'number' &&
    typeof v.itemId === 'number' &&
    typeof v.label === 'string' &&
    typeof v.key === 'string'
  );
};

/**
 * Read the saved set. `dirty` is deliberately NOT restored: unsaved input does
 * not survive a refresh, so a restored tab cannot be holding any.
 *
 * Falls back to the single-order keys the pane used before tabs existed
 * (`currentRow` + `openDetails`), so nobody's open order disappears on the
 * release that introduces the strip.
 */
export const readTabs = (
  storage: StorageLike | null | undefined,
  ownerId?: number | null
): OrderTabsState => {
  const raw = read(storage, ORDER_TABS_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<SavedOrderTabs>;
      // Someone else's tabs: forget them, legacy keys included, so nothing
      // below can fall through to the previous person's open order.
      if (isAnotherUsers(parsed.ownerId, ownerId)) {
        forgetTabs(storage);
        return emptyTabs();
      }
      const tabs = (Array.isArray(parsed.tabs) ? parsed.tabs : [])
        .filter(isTab)
        .slice(0, MAX_ORDER_TABS)
        .map((tab) => ({
          ...tab,
          merchant: typeof tab.merchant === 'string' ? tab.merchant : '',
          openedAt: typeof tab.openedAt === 'number' ? tab.openedAt : 0,
          viewedAt: typeof tab.viewedAt === 'number' ? tab.viewedAt : 0,
          innerTab: typeof tab.innerTab === 'string' && tab.innerTab ? tab.innerTab : 'timeline',
          dirty: false,
        }));
      const activeKey =
        typeof parsed.activeKey === 'string' && tabs.some((tab) => tab.key === parsed.activeKey)
          ? parsed.activeKey
          : (tabs[tabs.length - 1]?.key ?? null);
      return { tabs, activeKey };
    } catch {
      /* fall through to the legacy keys */
    }
  }

  // Legacy single-order state.
  if (read(storage, 'openDetails') !== 'true') return emptyTabs();
  try {
    const row = JSON.parse(read(storage, 'currentRow') ?? 'null') as {
      order_id?: number;
      id?: number;
    } | null;
    if (!row || typeof row.order_id !== 'number' || typeof row.id !== 'number') return emptyTabs();
    const key = tabKey(row.order_id, row.id);
    const innerTab = read(storage, 'activeDetailsTab') || 'timeline';
    return {
      tabs: [
        {
          key,
          orderId: row.order_id,
          itemId: row.id,
          label: '',
          merchant: '',
          openedAt: 0,
          viewedAt: 0,
          dirty: false,
          innerTab,
        },
      ],
      activeKey: key,
    };
  } catch {
    return emptyTabs();
  }
};

export const writeTabs = (
  storage: StorageLike | null | undefined,
  state: OrderTabsState,
  ownerId?: number | null
): void => {
  try {
    const saved: SavedOrderTabs = {
      tabs: state.tabs.map(({ dirty: _dirty, ...rest }) => rest),
      activeKey: state.activeKey,
    };
    if (typeof ownerId === 'number') saved.ownerId = ownerId;
    storage?.setItem(ORDER_TABS_KEY, JSON.stringify(saved));
  } catch {
    /* storage may be unavailable; the strip still works for this page load */
  }
};

/**
 * Drop every trace of open orders — the set and the single-order keys older
 * code still reads — so the next dashboard load starts with the pane closed.
 * Called when the saved set turns out to be another user's, and available to
 * anything that changes who is signed in.
 */
export const forgetTabs = (storage: StorageLike | null | undefined): void => {
  for (const key of [ORDER_TABS_KEY, 'currentRow', 'openDetails', 'activeDetailsTab']) {
    try {
      storage?.removeItem(key);
    } catch {
      /* nothing to forget, or nowhere to forget it */
    }
  }
};

/**
 * A saved set is another user's when both sides know who they are and they
 * differ. A set saved before ownership was recorded, or read by a caller who
 * does not know the user, is taken at face value — the strip must not empty
 * itself on the release that introduces the check.
 */
const isAnotherUsers = (savedOwner: number | null | undefined, ownerId: number | null | undefined): boolean =>
  typeof savedOwner === 'number' && typeof ownerId === 'number' && savedOwner !== ownerId;

const read = (storage: StorageLike | null | undefined, key: string): string | null => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};
